import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Button, VStack, Heading, Text, Textarea, HStack, useToast, Spinner, Divider, Badge,
  FormControl, FormLabel, Input, NumberInput, NumberInputField, Select, CheckboxGroup,
  Checkbox, Stack, RadioGroup, Radio, useDisclosure, AlertDialog, AlertDialogBody,
  AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, Center
} from '@chakra-ui/react';
import { AttachmentIcon, CheckIcon, EditIcon, RepeatIcon } from '@chakra-ui/icons';
import { getForm } from '../firebase.utils';
import axios from 'axios';
import mixpanel from 'mixpanel-browser';
import { useTranslation } from 'react-i18next';

const FormDraftPreview = ({ userToken, iframe, rootUrl }) => {
  const { runId } = useParams();
  const [formDoc, setFormDoc] = useState(null);
  const [formJSON, setFormJSON] = useState(null);
  const [rawResponseText, setRawResponseText] = useState('');
  const [showRawJson, setShowRawJson] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMode, setLoadingMode] = useState('loading_draft');
  const [iterateText, setIterateText] = useState('');
  const toast = useToast();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { t } = useTranslation();
  const { isOpen: isImportConfirmOpen, onOpen: onImportConfirmOpen, onClose: onImportConfirmClose } = useDisclosure();
  const importCancelRef = useRef();

  const extractRawTextFromOpenAI = (openAiData) => {
    if (!openAiData) return '';
    if (typeof openAiData.output_text === 'string') return openAiData.output_text;

    const output = Array.isArray(openAiData.output) ? openAiData.output : [];
    const messageChunk = output.find((entry) => entry?.type === 'message');
    const content = Array.isArray(messageChunk?.content) ? messageChunk.content : [];
    const outputTextChunk = content.find((c) => c?.type === 'output_text' && typeof c?.text === 'string');
    if (outputTextChunk?.text) return outputTextChunk.text;

    return '';
  };

  const safeParseForm = (rawText) => {
    if (!rawText) return null;
    try {
      const parsed = JSON.parse(
        rawText
          .replace(/`/g, '')
          .replace(/^json/, '')
          .trim()
          .replace(/^[\n\`]+|[\n\`]+$/g, '')
      );
      return parsed?.form || parsed;
    } catch (_) {
      return null;
    }
  };

  const hasItems = (candidate) => {
    const formCandidate = candidate?.form || candidate;
    return Array.isArray(formCandidate?.items) && formCandidate.items.length > 0;
  };

  const extractFormFromResponseEnvelope = (obj) => {
    if (!obj || typeof obj !== 'object') return null;

    // 1) Already a form object
    if (hasItems(obj)) return obj.form || obj;

    // 2) Some payloads store the response envelope under "form"
    if (obj.form && hasItems(obj.form)) return obj.form.form || obj.form;

    // 3) Extract nested output_text and parse again
    const outputs = Array.isArray(obj.output) ? obj.output : [];
    const msg = outputs.find((entry) => entry?.type === 'message');
    const content = Array.isArray(msg?.content) ? msg.content : [];
    const outText = content.find((c) => c?.type === 'output_text' && typeof c?.text === 'string');
    if (outText?.text) {
      const parsed = safeParseForm(outText.text);
      if (hasItems(parsed)) return parsed.form || parsed;
    }

    // 4) Same structure but nested under obj.form.output
    const nestedOutputs = Array.isArray(obj?.form?.output) ? obj.form.output : [];
    const nestedMsg = nestedOutputs.find((entry) => entry?.type === 'message');
    const nestedContent = Array.isArray(nestedMsg?.content) ? nestedMsg.content : [];
    const nestedOutText = nestedContent.find((c) => c?.type === 'output_text' && typeof c?.text === 'string');
    if (nestedOutText?.text) {
      const parsedNested = safeParseForm(nestedOutText.text);
      if (hasItems(parsedNested)) return parsedNested.form || parsedNested;
    }

    return null;
  };

  const questionTypeLabel = (type) => {
    const map = {
      text: t('draft_preview.question_types.text'),
      number: t('draft_preview.question_types.number'),
      date: t('draft_preview.question_types.date'),
      signature: t('draft_preview.question_types.signature'),
      barcode: t('draft_preview.question_types.barcode'),
      multi_photo: t('draft_preview.question_types.multi_photo'),
      photo: t('draft_preview.question_types.photo'),
      multi_option: t('draft_preview.question_types.multi_option'),
      select_check_metadata: t('draft_preview.question_types.select_check_metadata'),
      select_number_metadata: t('draft_preview.question_types.select_number_metadata'),
      select_text_metadata: t('draft_preview.question_types.select_text_metadata'),
      static_text: t('draft_preview.question_types.static_text'),
      select_metadata: t('draft_preview.question_types.select_metadata'),
      attachment: t('draft_preview.question_types.attachment'),
      table: t('draft_preview.question_types.table'),
      select_option_metadata_comments: t('draft_preview.question_types.select_option_metadata_comments')
    };
    return map[type] || type || t('draft_preview.question_types.undefined');
  };

  const getGroupedBySection = (form) => {
    const items = Array.isArray(form?.items) ? form.items : [];
    return items.reduce((acc, item) => {
      const section = item.label || t('draft_preview.no_section');
      if (!acc[section]) acc[section] = [];
      acc[section].push(item);
      return acc;
    }, {});
  };

  const renderQuestionPreview = (item, isSubQuestion = false) => {
    const options = Array.isArray(item.options) ? item.options : [];
    const listChoices = Array.isArray(item.listChoices) ? item.listChoices : [];

    let control = <Input size="sm" isDisabled placeholder={t('draft_preview.sample_value_placeholder')} />;

    switch (item.type) {
      case 'text':
        control = <Input size="sm" isDisabled placeholder={t('draft_preview.sample_text_placeholder')} />;
        break;
      case 'number':
        control = (
          <NumberInput size="sm" isDisabled>
            <NumberInputField placeholder={t('draft_preview.sample_number_placeholder')} />
          </NumberInput>
        );
        break;
      case 'date':
        control = (
          <Input
            size="sm"
            isDisabled
            type={item.dateMode === 'datetime' ? 'datetime-local' : 'date'}
          />
        );
        break;
      case 'multi_option':
        control = (
          <RadioGroup size="sm">
            <Stack direction={['column', 'row']} spacing={2}>
              {options.map((opt, idx) => (
                <Radio size="sm" key={`${item.id}-opt-${idx}`} isDisabled value={`${opt}`}>{`${opt}`}</Radio>
              ))}
            </Stack>
          </RadioGroup>
        );
        break;
      case 'select_metadata':
        control = (
          <Select size="sm" isDisabled placeholder={t('draft_preview.select_option_placeholder')}>
            {listChoices.map((opt, idx) => (
              <option key={`${item.id}-sel-${idx}`}>{opt}</option>
            ))}
          </Select>
        );
        break;
      case 'select_check_metadata':
        control = (
          <CheckboxGroup>
            <VStack align="stretch" spacing={0.5}>
              {listChoices.map((choice, idx) => (
                <Checkbox size="sm" key={`${item.id}-check-${idx}`} isDisabled>{choice}</Checkbox>
              ))}
            </VStack>
          </CheckboxGroup>
        );
        break;
      case 'select_text_metadata':
        control = (
          <VStack align="stretch" spacing={1.5}>
            {listChoices.map((choice, idx) => (
              <Input size="sm" key={`${item.id}-txt-${idx}`} isDisabled placeholder={choice} />
            ))}
          </VStack>
        );
        break;
      case 'select_number_metadata':
        control = (
          <VStack align="stretch" spacing={1.5}>
            {listChoices.map((choice, idx) => (
              <NumberInput size="sm" key={`${item.id}-num-${idx}`} isDisabled>
                <NumberInputField placeholder={choice} />
              </NumberInput>
            ))}
          </VStack>
        );
        break;
      case 'photo':
        control = (
          <Box borderWidth="1px" borderStyle="dashed" borderRadius="md" p={2}>
            <Button size="sm" leftIcon={<AttachmentIcon />} isDisabled variant="ghost">
              {t('draft_preview.actions.upload_photo')}
            </Button>
          </Box>
        );
        break;
      case 'multi_photo':
        control = (
          <Box borderWidth="1px" borderStyle="dashed" borderRadius="md" p={2}>
            <Button size="sm" leftIcon={<AttachmentIcon />} isDisabled variant="ghost">
              {t('draft_preview.actions.upload_photos')}
            </Button>
          </Box>
        );
        break;
      case 'signature':
        control = (
          <Box borderWidth="1px" borderStyle="dashed" borderRadius="md" p={2}>
            <Button size="sm" leftIcon={<EditIcon />} isDisabled variant="ghost">
              {t('draft_preview.actions.sign_here')}
            </Button>
          </Box>
        );
        break;
      case 'attachment':
        control = (
          <Box borderWidth="1px" borderStyle="dashed" borderRadius="md" p={2}>
            <Button size="sm" leftIcon={<AttachmentIcon />} isDisabled variant="ghost">
              {t('draft_preview.actions.upload_file')}
            </Button>
          </Box>
        );
        break;
      case 'barcode':
        control = (
          <Box borderWidth="1px" borderStyle="dashed" borderRadius="md" p={2}>
            <Button size="sm" leftIcon={<CheckIcon />} isDisabled variant="ghost">
              {t('draft_preview.actions.scan_code')}
            </Button>
          </Box>
        );
        break;
      case 'static_text':
        control = (
          <Box borderLeftWidth="3px" borderColor="blue.300" pl={3} py={1} bg="blue.50" borderRadius="sm">
            <Text fontSize="sm" color="blue.900">{t('draft_preview.actions.instruction_block')}</Text>
          </Box>
        );
        break;
      case 'table':
        control = (
          <Box borderWidth="1px" borderRadius="md" p={2} bg="gray.50">
            <Text fontSize="xs" fontWeight="medium" mb={2} color="gray.700">{t('draft_preview.repeatable_group_preview')}</Text>
            <VStack align="stretch" spacing={2}>
              {Object.values(item.questions || {}).map((subQ, idx) => (
                <FormControl key={`${item.id}-sub-${idx}`}>
                  {renderQuestionPreview(subQ, true)}
                </FormControl>
              ))}
            </VStack>
            <Button mt={2} size="xs" isDisabled leftIcon={<EditIcon />}>{t('draft_preview.add_row')}</Button>
          </Box>
        );
        break;
      default:
        break;
    }

    if (item.type === 'table') {
      return (
        <Box>
          <HStack spacing={2} wrap="wrap" mb={1}>
            <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>
              {isSubQuestion ? '- ' : ''}{item.name || t('draft_preview.unnamed_question')}
            </Text>
            <Badge colorScheme={isSubQuestion ? 'purple' : 'blue'} fontSize="0.65em">
              {questionTypeLabel(item.type)}
            </Badge>
          </HStack>
          {item.description && (
            <Text fontSize="xs" color="gray.500" mb={1.5} noOfLines={2}>{item.description}</Text>
          )}
          {control}
        </Box>
      );
    }

    return (
      <Box
        display="grid"
        gridTemplateColumns={{ base: '1fr', md: isSubQuestion ? '1.2fr 1.8fr' : '1fr 2fr' }}
        gap={2}
        alignItems="start"
      >
        <Box minW={0}>
          <HStack spacing={2} wrap="wrap" mb={1}>
            <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>
              {isSubQuestion ? '- ' : ''}{item.name || t('draft_preview.unnamed_question')}
            </Text>
            <Badge colorScheme={isSubQuestion ? 'purple' : 'blue'} fontSize="0.65em">
              {questionTypeLabel(item.type)}
            </Badge>
          </HStack>
          {item.description && (
            <Text fontSize="xs" color="gray.500" noOfLines={2}>{item.description}</Text>
          )}
        </Box>
        <Box>{control}</Box>
      </Box>
    );
  };

  const load = async () => {
    setLoadingMode('loading_draft');
    setIsLoading(true);
    try {
      const data = await getForm(runId, userToken);
      setFormDoc(data);
      const rawText = extractRawTextFromOpenAI(data.openAiData);
      setRawResponseText(rawText);
      const parsedFromText = safeParseForm(rawText);
      const extractedFromEnvelope = extractFormFromResponseEnvelope(parsedFromText);
      const extractedFromStored = extractFormFromResponseEnvelope(data.formData);
      const candidate = extractedFromStored || extractedFromEnvelope || data.formData || parsedFromText || null;
      setFormJSON(candidate?.form || candidate || null);
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error loading draft',
        description: e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setLoadingMode('idle');
    }
  };

  useEffect(() => {
    load();
  }, [runId, userToken]);

  const handleIterate = async () => {
    if (!iterateText.trim()) {
      toast({
        title: t('draft_preview.iterate_required_title'),
        description: t('draft_preview.iterate_required_description'),
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }
    if (!formJSON) return;
    try {
      setLoadingMode('iterating');
      setIsLoading(true);
      const resp = await axios.post(`${backendUrl}generateFormWithResponses`, {
        userToken,
        message: iterateText,
        previous_output: JSON.stringify({ form: formJSON }),
        runId
      });
      await load();
      setIterateText('');
      try { mixpanel.track('Form Iterated', { runId }); } catch (_) {}
      toast({
        title: t('draft_preview.form_improved_title'),
        description: t('draft_preview.form_improved_description'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (e) {
      console.error(e);
      toast({
        title: t('draft_preview.error_iterating_title'),
        description: e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setLoadingMode('idle');
    }
  };

  const handleImport = async () => {
    try {
      setLoadingMode('importing');
      setIsLoading(true);
      const resp = await axios.post(`${backendUrl}importFormDraft`, {
        userToken,
        runId
      });
      if (resp.data?.success) {
        try { mixpanel.track('Form Imported', { runId }); } catch (_) {}
        toast({
          title: t('draft_preview.form_imported_title'),
          description: t('draft_preview.form_imported_description'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        navigate(`/${iframe ? 'iframe/' : ''}form/${runId}`);
      } else {
        throw new Error('Import failed');
      }
    } catch (e) {
      console.error(e);
      const serverData = e?.response?.data || {};
      const knownCodeKey = serverData.code
        ? `draft_preview.import_errors.${serverData.code}`
        : null;

      const translated = knownCodeKey ? t(knownCodeKey, { defaultValue: '' }) : '';
      const description = translated
        || serverData.userMessage
        || serverData.details?.error
        || serverData.message
        || e.message;

      toast({
        title: t('draft_preview.error_importing_title'),
        description,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setLoadingMode('idle');
    }
  };

  const getLoadingTitle = () => {
    if (loadingMode === 'importing') return t('draft_preview.importing_title');
    if (loadingMode === 'iterating') return t('draft_preview.iterating_title');
    return t('draft_preview.loading_title');
  };

  const getLoadingDescription = () => {
    if (loadingMode === 'importing') return t('draft_preview.importing_description');
    if (loadingMode === 'iterating') return t('draft_preview.iterating_description');
    return t('please_wait');
  };

  if (isLoading) {
    return (
      <Center minH="65vh" w="100%">
        <Box
          bg="white"
          borderWidth="1px"
          borderColor="blue.100"
          borderRadius="2xl"
          p={{ base: 6, md: 8 }}
          boxShadow="sm"
          textAlign="center"
          maxW="560px"
          w="100%"
        >
          <Spinner size="xl" thickness="4px" speed="0.65s" mb={6} />
          <Heading mb={2}>{getLoadingTitle()}</Heading>
          <Text color={'gray.500'}>{getLoadingDescription()}</Text>
        </Box>
      </Center>
    );
  }

  if (!formJSON) {
    return (
      <Center minH="45vh" w="100%">
        <Box
          bg="white"
          borderWidth="1px"
          borderColor="orange.200"
          borderRadius="2xl"
          p={{ base: 5, md: 7 }}
          boxShadow="sm"
          textAlign="center"
          maxW="760px"
          w="100%"
        >
          <Heading as="h3" size="md" mb={2}>{t('draft_preview.no_draft')}</Heading>
          <Text color="gray.600" mb={2}>
            No valid questions were found in this draft output.
          </Text>
          {formDoc?.error && (
            <Text color="orange.600" fontSize="sm">
              {formDoc.error}
            </Text>
          )}
        </Box>
      </Center>
    );
  }
  const groupedSections = getGroupedBySection(formJSON);
  const jsonToShow = rawResponseText || JSON.stringify({ form: formJSON }, null, 2);

  return (
    <VStack spacing={6} align="stretch" maxW="1200px" mx="auto" w="100%">
      <Heading as="h1">{formJSON.title || t('draft_preview.default_title')}</Heading>

      <Box p={{ base: 4, md: 5 }} borderWidth="1px" borderColor="blue.100" bg="white" borderRadius="2xl" boxShadow="sm">
        <Heading as="h3" size="md" mb={3}>{t('draft_preview.questions_title')}</Heading>
        {Object.keys(groupedSections).length === 0 && (
          <Text color="gray.500">{t('draft_preview.no_questions')}</Text>
        )}
        <VStack align="stretch" spacing={3}>
          {Object.entries(groupedSections).map(([sectionName, items]) => (
            <Box key={sectionName} borderWidth="1px" borderColor="gray.100" bg="gray.50" borderRadius="lg" p={3}>
              <Heading as="h4" size="sm" mb={3}>{sectionName || t('draft_preview.no_section')}</Heading>
              <VStack align="stretch" spacing={2.5}>
                {items.map((item, idx) => (
                  <FormControl key={`${item.id || item.name || 'q'}-${idx}`}>
                    <FormLabel mb={1}></FormLabel>
                    {renderQuestionPreview(item)}
                  </FormControl>
                ))}
              </VStack>
            </Box>
          ))}
        </VStack>
        <Divider my={4} />
        <HStack justify="space-between" mb={2}>
          <Heading as="h4" size="sm">{t('draft_preview.raw_json_title')}</Heading>
          <Button size="sm" variant="outline" onClick={() => setShowRawJson((prev) => !prev)}>
            {showRawJson ? t('draft_preview.hide') : t('draft_preview.show_more')}
          </Button>
        </HStack>
        {showRawJson && (
          <Box as="pre" p={3} overflow="auto" bg="gray.50" borderWidth="1px" borderRadius="md">
            {jsonToShow}
          </Box>
        )}
      </Box>

      <Box p={{ base: 4, md: 5 }} borderWidth="1px" borderColor="blue.100" bg="white" borderRadius="2xl" boxShadow="sm">
        <Heading as="h3" size="md" mb={3}>{t('draft_preview.iterate_title')}</Heading>
        <Textarea
          value={iterateText}
          onChange={(e) => setIterateText(e.target.value)}
          placeholder={t('draft_preview.iterate_placeholder')}
          mb={3}
        />
        <HStack>
          <Button
            variant="outline"
            leftIcon={<RepeatIcon />}
            onClick={handleIterate}
            isDisabled={!iterateText.trim()}
            isLoading={isLoading}
          >
            {t('draft_preview.apply_improvements')}
          </Button>
          <Button
            colorScheme="blue"
            leftIcon={<CheckIcon />}
            onClick={onImportConfirmOpen}
            isLoading={isLoading}
          >
            {t('draft_preview.import_to_datascope')}
          </Button>
        </HStack>
      </Box>

      <AlertDialog
        isOpen={isImportConfirmOpen}
        leastDestructiveRef={importCancelRef}
        onClose={onImportConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('draft_preview.import_confirm_title')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('draft_preview.import_confirm_description')}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={importCancelRef} onClick={onImportConfirmClose}>
                {t('draft_preview.cancel')}
              </Button>
              <Button
                colorScheme="blue"
                onClick={() => {
                  onImportConfirmClose();
                  handleImport();
                }}
                ml={3}
              >
                {t('draft_preview.confirm_import')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};

export default FormDraftPreview;

